<template>
  <div style="background-color: var(--color-surface)">
    <template>
      <v-list-item
        v-for="setting in filteredAlertSettings"
        :key="setting.alert.id"
        class="mb-2"
      >
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ setting.alert.summary }}
                </v-list-item-title>
                <div class="d-flex align-center">
                  <v-list-item-subtitle class="alert-description">
                    {{ setting.alert.description }}
                  </v-list-item-subtitle>
                </div>
              </v-list-item-content>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="pa-4">
                <div
                  v-if="!setting.applyToAllVessels"
                  class="mb-2"
                >
                  <v-combobox
                    v-model="setting.selectedVessels"
                    :items="setting.alert.vessels"
                    item-text="vessel_name"
                    item-value="vesselId"
                    label="Select the vessels to apply the changes"
                    multiple
                    chips
                    deletable-chips
                    small-chips
                    return-object
                    class="themed-combobox"
                  />
                </div>
                <div class="mb-4">
                  <v-switch
                    v-model="setting.alert.isActive"
                    :label="getActivationLabel(setting.alert.isActive)"
                    class="switch-alert"
                    inset
                  />
                </div>
                <div class="d-flex">
                  <ThemedTextField
                    v-model.number="setting.alert.fleetLowerLimit"
                    type="number"
                    label="Lower Limit"
                    class="mr-4"
                    outlined
                  />
                  <ThemedTextField
                    v-model.number="setting.alert.fleetUpperLimit"
                    type="number"
                    label="Upper Limit"
                    outlined
                  />
                </div>
                <div class="d-flex align-center">
                  <ThemedCheckbox
                    v-model="setting.applyToAllVessels"
                    label="Apply to all vessels"
                    :input-value="setting.applyToAllVessels"
                  />
                  <v-spacer />
                  <animated-success-button
                    :loading="updateLoading"
                    :success="showSuccess"
                    :disabled="isButtonDisabled(setting)"
                    @click="saveAlertSetting(setting)"
                  >
                    Apply
                  </animated-success-button>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-list-item>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import AnimatedSuccessButton from "@/core/AnimatedSuccessButton.vue";
import ThemedCheckbox from "@/core/ThemedCheckbox.vue";
import ThemedTextField from "@/core/ThemedTextField.vue";
export default {
  name: "FleetAlertsList",
  components: {
    AnimatedSuccessButton,
    ThemedCheckbox,
    ThemedTextField,
  },
  data() {
    return {
      showSuccess: false,
    };
  },
  computed: {
    ...mapState("fleetAlerts", [
      "alerts",
      "selectedCategory",
      "categories",
      "updateLoading",
      "updateSuccess",
    ]),
    ...mapGetters("fleetAlerts", ["filteredAlertSettings"]),
    currentCategory() {
      return this.categories.find((cat) => cat.id === this.selectedCategory);
    },
  },
  watch: {
    updateSuccess: {
      handler(value) {
        this.showSuccess = value;
      },
      deep: true,
    },
  },
  methods: {
    getActivationLabel(isActive) {
      return isActive ? "Deactivate for vessels" : "Activate for vessels";
    },
    saveAlertSetting(alertSetting) {
      this.$store.dispatch("fleetAlerts/updateAlerts", alertSetting);
    },
    isButtonDisabled(setting) {
      return setting.selectedVessels.length === 0 && !setting.applyToAllVessels;
    },
  },
};
</script>

      <style scoped>
.v-list {
  background-color: var(--color-surface);
  position: relative;
}
.alert-description {
  color: var(--color-text-muted) !important;
}

/* Theme the alert switch */
.switch-alert >>> .v-label {
  color: var(--color-text) !important;
}

/* Theme the combobox */
.themed-combobox >>> .v-input__slot {
  background-color: var(--color-surface-light) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-md) !important;
  transition: var(--transition-default);
  padding: 12px 16px;
}

.themed-combobox >>> .v-input__slot:hover {
  border-color: var(--color-secondary-light) !important;
}

.themed-combobox >>> .v-input--is-focused .v-input__slot {
  border-color: var(--color-secondary) !important;
}

.themed-combobox >>> .v-label {
  color: var(--color-text-muted) !important;
  font-weight: 500;
  font-size: 14px;
}

.themed-combobox >>> .v-input--is-focused .v-label {
  color: var(--color-secondary) !important;
}

.themed-combobox >>> .v-select__selections {
  color: var(--color-text) !important;
  font-weight: 500;
  font-size: 14px;
}

.themed-combobox >>> .v-select__selection {
  color: var(--color-text) !important;
}

.themed-combobox >>> input {
  color: var(--color-text) !important;
  font-weight: 500;
  font-size: 14px;
}

.themed-combobox >>> .v-chip {
  background-color: var(--color-secondary-light) !important;
  color: var(--color-text) !important;
  border-radius: var(--radius-sm) !important;
  margin: 2px 4px 2px 0;
}

.themed-combobox >>> .v-chip__close {
  color: var(--color-text) !important;
}

.themed-combobox >>> .v-chip .v-icon {
  color: var(--color-text) !important;
}

.fixed-header {
  position: sticky;
  top: 0;
  background-color: var(--color-surface);
  z-index: 1;
}

/* Theme the expansion panels */
.v-expansion-panels >>> .v-expansion-panel {
  background-color: var(--color-surface) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-md) !important;
  margin-bottom: 8px;
}

.v-expansion-panels >>> .v-expansion-panel-header {
  background-color: var(--color-surface) !important;
  color: var(--color-text) !important;
  border-radius: var(--radius-md) !important;
  padding: 16px 20px;
}

.v-expansion-panels >>> .v-expansion-panel-header:hover {
  background-color: var(--color-surface-light) !important;
}

.v-expansion-panels >>> .v-expansion-panel-header--active {
  background-color: var(--color-surface-light) !important;
  border-bottom: 1px solid var(--color-border) !important;
}

.v-expansion-panels >>> .v-expansion-panel-content {
  background-color: var(--color-surface) !important;
  border-top: 1px solid var(--color-border) !important;
}

.v-expansion-panels >>> .v-expansion-panel-content__wrap {
  padding: 20px;
  color: var(--color-text) !important;
}

/* Theme list item titles and subtitles */
.v-list-item-title {
  color: var(--color-text) !important;
  font-weight: 500;
}

.v-list-item-subtitle {
  color: var(--color-text-muted) !important;
}

.v-expansion-panels >>> .v-expansion-panel-header__icon:hover {
  color: var(--color-secondary-light) !important;
}

.v-expansion-panels >>> .v-expansion-panel-header__icon .v-icon {
  color: var(--color-secondary) !important;
  transition: transform var(--transition-default) !important;
  transform: rotate(0deg) !important;
}

.v-expansion-panels
  >>> .v-expansion-panel-header--active
  .v-expansion-panel-header__icon {
  color: var(--color-secondary) !important;
  transform: rotate(-180deg) !important;
}

.v-expansion-panels >>> .v-expansion-panel-header {
  position: relative;
}

.v-expansion-panels >>> .v-expansion-panel-header__icon::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: transparent;
  transition: var(--transition-default);
  z-index: -1;
}

.v-expansion-panels
  >>> .v-expansion-panel-header:hover
  .v-expansion-panel-header__icon::before {
  background-color: var(--color-secondary-light);
  opacity: 0.1;
}

.v-expansion-panels
  >>> .v-expansion-panel-header--active
  .v-expansion-panel-header__icon::before {
  background-color: var(--color-secondary);
  opacity: 0.15;
}
</style>
